import { getCurrencySymbol } from '@src/site/currency';
import { type ReactNode } from 'react';
import { Trans } from 'react-i18next';
import type { TFunction } from 'i18next';

export type IdentitySnapshot = { name: string; pictureId: number | null; avatar: string | null };
export type Value = string | number | boolean | null;
export type Activity = {
  id: number;
  cardId?: number | null;
  actorCardId?: number | null;
  actorType?: 'organizer' | 'participant' | 'system';
  type: string;
  createdAt: string;
  participantName: string | null;
  participantPicture?: string | null;
  participantAvatar?: string | null;
  metadata: {
    thanked?: boolean;
    boxCount?: number;
    questionNumber?: number;
    questionTitle?: string;
    questionType?: string;
    operation?: string;
    exclusionParticipants?: Record<string, IdentitySnapshot>;
    exclusionNames?: Record<string, string>;
    reason?: string;
    fields?: string[];
    presenceBefore?: Record<string, boolean>;
    presenceAfter?: Record<string, boolean>;
    firstFill?: boolean;
    nameBefore?: string;
    nameAfter?: string;
    actor?: IdentitySnapshot | null;
    participant?: IdentitySnapshot;
    receiver?: IdentitySnapshot;
    receiverCardId?: number;
    method?: 'link' | 'email';
    recipientRole?: 'organizer' | 'participant';
    isOrganizer?: boolean;
    assignments?: { previousWardId?: number; previousRecipient?: IdentitySnapshot; cardId: number; wardId: number; cardName: string; wardName: string; giver?: IdentitySnapshot; recipient?: IdentitySnapshot }[];
    senderName?: string;
    receiverName?: string;
    direction?: 'santa' | 'giftee' | 'organizer' | 'participant';
    hasAttachment?: boolean;
    messageCount?: number;
    additional?: boolean;
    address?: string;
    email?: string;
    status?: string;
    theme?: string;
    previousTheme?: string;
    pictureId?: number;
    logo?: string | null;
    settings?: Record<string, Value>;
    budgetBefore?: Record<string, Value>;
    budgetAfter?: Record<string, Value>;
    name?: string;
    before?: Record<string, Value>;
    after?: Record<string, Value>;
  };
};

type Presentation = {
  title: ReactNode;
  regularTitle?: boolean;
  description?: ReactNode;
  changes: [string, Value][];
};
type RenderValue = (field: string, value: Value, theme?: string, avatar?: string | null) => ReactNode;
type RenderPerson = (name: ReactNode, role?: 'sender' | 'receiver') => ReactNode;
type EventFormatter = (event: Activity, t: TFunction, renderValue: RenderValue, renderPerson: RenderPerson) => Presentation;
function Person({ children, render, role }: { children?: ReactNode; render: RenderPerson; role?: 'sender' | 'receiver' }) {
  return <>{render(children, role)}</>;
}
const personComponents = (render: RenderPerson) => ({
  emphasis: <b />, person: <Person render={render} />, sender: <Person render={render} role="sender" />, receiver: <Person render={render} role="receiver" />,
});

const changesFor = (event: Activity): [string, Value][] =>
  Object.entries(event.metadata.before ?? {}).filter(([field, before]) =>
    field !== 'logo' || (before || null) !== (event.metadata.after?.[field] || null),
  );

// Each event owns its wording and which details are already covered by the title.
const giftActivity = (key: string): EventFormatter => (event, t, _renderValue, renderPerson) => ({
  title: <Trans t={t} i18nKey={key}
    values={{ name: event.metadata.senderName || event.participantName || t('boxActivity.participant') }}
    components={personComponents(renderPerson)} />,
  changes: [],
});

const eventTitle = (t: TFunction, key: string, values: Record<string, string>, renderPerson: RenderPerson = (name) => <b>{name}</b>) => (
  <Trans t={t} i18nKey={key} values={values} components={personComponents(renderPerson)} />
);

const eventFormatters: Record<string, EventFormatter> = {
  'box_gift.received': (event, t, renderValue, renderPerson) => giftActivity(event.metadata.thanked ? 'boxActivity.boxGiftThanked' : 'boxActivity.boxGiftReceived')(event, t, renderValue, renderPerson),
  'box_gift.sent': (event, t, _renderValue, renderPerson) => ({
    title: <Trans t={t} i18nKey="boxActivity.boxGiftSent" count={event.metadata.boxCount ?? 0} values={{ name: event.participantName || t('boxActivity.participant'), count: event.metadata.boxCount ?? 0 }} components={personComponents(renderPerson)} />,
    changes: [],
  }),
  'box.premium_address_requested': (event, t) => ({
    title: eventTitle(t, 'boxActivity.customAddressRequested', { address: event.metadata.address || '—' }),
    changes: [],
  }),
  'organizer.name_changed': (event, t, _renderValue, renderPerson) => ({
    title: eventTitle(t, 'boxActivity.organizerNameChanged', { name: event.metadata.nameAfter || event.participantName || t('boxActivity.participant') }, renderPerson),
    changes: [],
  }),
  'participant.card_claimed': (event, t, _renderValue, renderPerson) => ({
    title: eventTitle(t, 'boxActivity.cardClaimed', {
      name: event.participantName || t('boxActivity.participant'),
      method: t(`boxActivity.claimMethod.${event.metadata.method === 'email' ? 'email' : 'link'}`),
    }, renderPerson),
    changes: [],
  }),
  'participant.card_updated': (event, t, renderValue, renderPerson) => ({
    title: eventTitle(t, event.metadata.isOrganizer ? 'boxActivity.cardUpdatedByOrganizer' : event.metadata.firstFill ? (event.actorType === 'organizer' && event.actorCardId === event.cardId ? 'boxActivity.ownCardFilled' : 'boxActivity.cardFilled') : 'boxActivity.cardUpdated', { name: event.participantName || t('boxActivity.participant') }, renderPerson),
    description: <dl className="box-activity__initial-settings">
      {(event.metadata.fields || []).filter((field) => !(field === 'avatar' && event.metadata.fields?.includes('pictureId') && event.metadata.before && event.metadata.after)).map((field) => {
        const nameChange = field === 'username' && typeof event.metadata.nameBefore === 'string' && typeof event.metadata.nameAfter === 'string';
        const pictureChange = (field === 'pictureId' || field === 'avatar') && event.metadata.before && event.metadata.after;
        return <div key={field}>
          <dt>{t(`boxActivity.cardFields.${field}`)}</dt>
          <dd className="box-activity__setting-values">
            {nameChange ? <><del>{event.metadata.nameBefore || '—'}</del><span>{event.metadata.nameAfter || '—'}</span></>
              : pictureChange ? <>
                <del className="box-activity__previous-picture">{renderValue('participantPictureId', event.metadata.before!.pictureId ?? null, undefined, event.metadata.before!.avatar as string | null)}</del>
                <span>{renderValue('participantPictureId', event.metadata.after!.pictureId ?? null, undefined, event.metadata.after!.avatar as string | null)}</span>
              </> : <span>{t(event.metadata.presenceBefore?.[field] === false && event.metadata.presenceAfter?.[field] === true ? 'boxActivity.cardFieldAdded' : event.metadata.presenceBefore?.[field] === true && event.metadata.presenceAfter?.[field] === false ? 'boxActivity.cardFieldRemoved' : 'boxActivity.cardFieldUpdated')}</span>}
          </dd>
        </div>;
      })}
    </dl>,
    changes: [],
  }),
  'participant.joined': (event, t, _value, person) => ({ title: eventTitle(t, 'boxActivity.participantJoined', { name: event.participantName || t('boxActivity.participant') }, person), changes: [] }),
  'participant.left': (event, t, _value, person) => ({ title: eventTitle(t, 'boxActivity.participantLeft', { name: event.participantName || t('boxActivity.participant') }, person), changes: [] }),
  'participant.giftee_changed': (event, t, _value, person) => ({ title: eventTitle(t, 'boxActivity.gifteeChangedAfterDeparture', { name: event.participantName || t('boxActivity.participant') }, person), changes: [] }),
  'participant.giftee_revealed': giftActivity('boxActivity.gifteeRevealed'),
  'gift.sent': giftActivity('boxActivity.giftSent'),
  'gift.received': giftActivity('boxActivity.giftReceived'),
  'gift.sent_undone': giftActivity('boxActivity.giftSentUndone'),
  'gift.received_undone': giftActivity('boxActivity.giftReceivedUndone'),
  'message.sent': (event, t, _renderValue, renderPerson) => ({
    title: <>
      <Trans t={t} i18nKey={`boxActivity.${(event.metadata.messageCount || 1) > 1 ? 'messagesTo' : 'messageTo'}.${event.metadata.direction || 'participant'}`} count={event.metadata.messageCount || 1}
        values={{ messages: t('boxActivity.messageCount', { count: event.metadata.messageCount || 1 }), sender: event.metadata.actor?.name || event.metadata.senderName || event.participantName || t('boxActivity.participant'), receiver: event.metadata.receiverName || t('boxActivity.participant') }}
        components={{ ...personComponents(renderPerson), ...(event.metadata.direction === 'participant' ? { sender: <b /> } : {}) }} />
      {event.metadata.hasAttachment && <> {t('boxActivity.withAttachment')}</>}
    </>,
    changes: [],
  }),
  'invitation.link_copied': (event, t, _renderValue, renderPerson) => ({
    title: eventTitle(t, 'boxActivity.personalLinkCopied', { name: event.participantName || t('boxActivity.participant') }, renderPerson),
    changes: [],
  }),
  'invitation.email_queued': (event, t, _renderValue, renderPerson) => {
    const values = {
      name: event.participantName || t('boxActivity.participant'),
      email: event.metadata.email || '—',
    };
    if (event.metadata.status === 'sent') {
      return {
        title: <Trans t={t} i18nKey={event.metadata.recipientRole === 'organizer' ? 'boxActivity.organizerInvitationSent' : 'boxActivity.invitationSent'} values={values} components={personComponents(renderPerson)} />,
        changes: [],
      };
    }
    return {
      title: t(event.metadata.status === 'error' ? 'boxActivity.invitationFailed' : 'boxActivity.invitationQueued'),
      description: <Trans t={t} i18nKey={event.metadata.recipientRole === 'organizer' ? 'boxActivity.organizerInvitationRecipient' : 'boxActivity.invitationRecipient'} values={values} components={personComponents(renderPerson)} />,
      changes: [],
    };
  },
  'box.draw_completed': (event, t) => ({ title: t(event.metadata.additional ? 'boxActivity.additionalDrawCompleted' : 'boxActivity.drawCompleted'), changes: [] }),
  'box.draw_reset': (_event, t) => ({ title: t('boxActivity.drawReset'), changes: [] }),
  'box.archived': (_event, t) => ({ title: t('boxActivity.archived'), changes: [] }),
  'box.unarchived': (_event, t) => ({ title: t('boxActivity.unarchived'), changes: [] }),
  'box.deleted': (_event, t) => ({ title: t('boxActivity.deleted'), changes: [] }),
  'participant.added': (event, t, _renderValue, renderPerson) => ({
    title: eventTitle(t, 'boxActivity.events.participant.added', {
      name: event.participantName || t('boxActivity.participant'),
    }, renderPerson),
    changes: [],
  }),
  'box.created': (event, t) => {
    const mode = event.metadata.settings?.mode;
    return {
      title: eventTitle(t, mode === 'full' || mode === 'quick'
        ? `boxActivity.created.${mode}` : 'boxActivity.events.box.created', {
        name: event.metadata.name || t('boxActivity.participant'),
      }),
      changes: [],
    };
  },
  'box.settings_updated': (event, t, renderValue, renderPerson) => {
    const before = event.metadata.before ?? {};
    const after = event.metadata.after ?? {};
    const changes = changesFor(event);
    const emphasized = (key: string, values?: Record<string, string>) => (
      <Trans t={t} i18nKey={key} values={values} components={personComponents(renderPerson)} />
    );
    if (typeof before.name === 'string' && typeof after.name === 'string' && before.name !== after.name) {
      return {
        title: emphasized('boxActivity.nameChanged', { before: before.name, after: after.name }),
        changes: changes.filter(([field]) => field !== 'name'),
      };
    }
    if (before.mode === 'quick' && after.mode === 'full') {
      return {
        title: emphasized('boxActivity.convertedToFull'),
        changes: changes.filter(([field]) => field !== 'mode'),
      };
    }
    if (changes.some(([field]) => field === 'theme')) {
      const previousTheme = before.theme || event.metadata.previousTheme;
      const nextTheme = after.theme || event.metadata.theme;
      const themeName = (theme: Value | undefined) =>
        typeof theme === 'string' && theme
          ? t(`boxActivity.values.${theme}`, { defaultValue: theme })
          : '—';
      return {
        title: emphasized('boxActivity.themeChanged', {
          before: themeName(previousTheme),
          after: themeName(nextTheme),
        }),
        changes: changes.filter(([field]) => field !== 'theme'),
      };
    }
    if (changes.some(([field]) => field === 'pictureId' || field === 'logo')) {
      const previousLogo = 'logo' in before ? before.logo : event.metadata.logo;
      const nextLogo = 'logo' in after ? after.logo : event.metadata.logo;
      const previousPicture = before.pictureId ?? event.metadata.pictureId ?? null;
      const nextPicture = after.pictureId ?? event.metadata.pictureId ?? null;
      return {
        title: <Trans t={t} i18nKey="boxActivity.pictureChanged" components={{
          before: <span>{renderValue(previousLogo ? 'logo' : 'pictureId', previousLogo || previousPicture,
            event.metadata.previousTheme || event.metadata.theme)}</span>,
          after: <span>{renderValue(nextLogo ? 'logo' : 'pictureId', nextLogo || nextPicture,
            event.metadata.theme)}</span>,
        }} />,
        changes: changes.filter(([field]) => field !== 'pictureId' && field !== 'logo'),
      };
    }
    const budgetFields = ['useCashLimit', 'cashLimitMin', 'cashLimit', 'cashLimitCurrency'];
    if (changes.some(([field]) => budgetFields.includes(field))) {
      const oldBudget = event.metadata.budgetBefore ?? before;
      const newBudget = event.metadata.budgetAfter ?? after;
      const range = (budget: Record<string, Value>) => {
        const amount = (v: Value | undefined) => typeof v === 'number'
          ? renderValue('budgetAmount', v) : '—';
        const currency = typeof budget.cashLimitCurrency === 'string'
          ? (getCurrencySymbol(budget.cashLimitCurrency) || budget.cashLimitCurrency.toUpperCase()) : '—';
        return `${amount(budget.cashLimitMin)} - ${amount(budget.cashLimit)} ${currency}`;
      };
      const title = newBudget.useCashLimit === false
        ? emphasized('boxActivity.budgetDisabled')
        : oldBudget.useCashLimit === false && newBudget.useCashLimit === true
          ? emphasized('boxActivity.budgetEnabled', { range: range(newBudget) })
          : emphasized('boxActivity.budgetChanged', { before: range(oldBudget), after: range(newBudget) });
      return { title, changes: changes.filter(([field]) => !budgetFields.includes(field)) };
    }
    if (changes.some(([field]) => field === 'useCircleDraw') && typeof after.useCircleDraw === 'boolean') {
      return {
        title: emphasized(after.useCircleDraw ? 'boxActivity.circleDrawEnabled' : 'boxActivity.circleDrawDisabled'),
        changes: changes.filter(([field]) => field !== 'useCircleDraw'),
      };
    }
    if (changes.some(([field]) => field === 'useWish') && typeof after.useWish === 'boolean') {
      return {
        title: <Trans t={t} i18nKey={after.useWish ? 'boxActivity.letterEnabled' : 'boxActivity.letterDisabled'} components={{ emphasis: <b /> }} />,
        changes: changes.filter(([field]) => field !== 'useWish'),
      };
    }
    if (changes.some(([field]) => field === 'useNames') && typeof after.useNames === 'boolean') {
      return {
        title: emphasized(after.useNames ? 'boxActivity.namesEnabled' : 'boxActivity.namesDisabled'),
        changes: changes.filter(([field]) => field !== 'useNames'),
      };
    }
    if (changes.some(([field]) => field === 'usePost') && typeof after.usePost === 'boolean') {
      return {
        title: emphasized(after.usePost ? 'boxActivity.addressEnabled' : 'boxActivity.addressDisabled'),
        changes: changes.filter(([field]) => field !== 'usePost'),
      };
    }
    if (changes.some(([field]) => field === 'isPhoneRequired') && typeof after.isPhoneRequired === 'boolean') {
      return {
        title: emphasized(after.isPhoneRequired ? 'boxActivity.phoneEnabled' : 'boxActivity.phoneDisabled'),
        changes: changes.filter(([field]) => field !== 'isPhoneRequired'),
      };
    }
    return { title: t('boxActivity.events.box.settings_updated'), changes };
  },
};

// Management actions name their historical actor, independently of the subject card.
function organizerTitle(event: Activity, t: TFunction, renderPerson: RenderPerson, viewerCardId?: number | null): ReactNode | undefined {
  if (event.type === 'invitation.email_queued' && event.metadata.status === 'sent') return undefined;
  const isSelf = viewerCardId != null && viewerCardId > 0 && event.actorCardId === viewerCardId && event.actorType === 'organizer';
  if (isSelf && event.type === 'message.sent' && event.metadata.direction === 'participant') {
    return <><Trans t={t} i18nKey={(event.metadata.messageCount || 1) > 1 ? 'boxActivity.ownMessages' : 'boxActivity.ownMessage'}
      values={{ messages: t('boxActivity.messageCount', { count: event.metadata.messageCount || 1 }), receiver: event.metadata.receiverName || t('boxActivity.participant') }}
      components={personComponents(renderPerson)} />{event.metadata.hasAttachment && <> {t('boxActivity.withAttachment')}</>}</>;
  }
  const actions: Record<string, string> = {
    'participant.giftee_changed': event.metadata.reason === 'manual' ? (event.metadata.assignments?.every((pair) => !pair.previousWardId && pair.wardId) ? 'assignGiftee' : 'changeGiftee') : '',
    'box.premium_address_requested': 'requestAddress',
    'box.settings_updated': 'settings',
    'box.exclusions_updated': 'exclusions',
    'box.draw_updated': 'updateDraw',
    'box.draw_completed': event.metadata.additional ? 'additionalDraw' : 'draw',
    'box.draw_reset': 'reset', 'box.archived': 'archive',
    'box.unarchived': 'unarchive', 'box.deleted': 'delete',
    'box.created': event.metadata.settings?.mode === 'quick' ? 'createQuick' : 'create',
    'participant.added': 'addParticipant',
    'participant.removed': 'removeParticipant',
    'invitation.link_copied': 'copyLink',
    'invitation.email_queued': event.metadata.status === 'sent' ? 'inviteSent' : event.metadata.status === 'error' ? 'inviteFailed' : 'inviteQueued',
    'organizer.name_changed': 'rename',
  };
  let action = actions[event.type];
  if (event.type === 'participant.card_updated' && event.actorType === 'organizer') {
    action = event.metadata.isOrganizer ? 'updateCard' : event.metadata.firstFill ? 'fillOwnCard' : 'updateOwnCard';
  }
  if (!action) return undefined;
  const actorName = event.metadata.actor?.name;
  return <Trans t={t} i18nKey={`boxActivity.${isSelf ? 'ownOrganizerActions' : 'organizerActions'}.${action}`}
    values={{ address: event.metadata.address || '—', name: event.participantName || t('boxActivity.participant'), game: event.metadata.name || '—', newName: event.metadata.nameAfter || '—' }}
    components={{ ...personComponents(renderPerson), organizer: <span>{t('boxActivity.organizer')}{actorName && <> <b>{actorName}</b></>}</span> }} />;
}

export function presentActivity(event: Activity, t: TFunction, renderValue: RenderValue, renderPerson: RenderPerson = (name) => <b>{name}</b>, viewerCardId?: number | null, renderIdentity?: (id: number, name: string, snapshot?: IdentitySnapshot) => ReactNode): Presentation {
  if (event.type === 'question.updated') {
    const builtin = ['santa_letter', 'phone', 'postal_address', 'pickup_point', 'delivery_address'].includes(event.metadata.questionType ?? '');
    const title = builtin ? t(`questions.types.${event.metadata.questionType}`) : event.metadata.questionTitle;
    const question = event.metadata.questionNumber ? `${event.metadata.questionNumber}. ${title}` : title;
    return {title: <Trans t={t} i18nKey={`boxActivity.${viewerCardId != null && event.actorCardId === viewerCardId ? 'ownQuestion' : 'question'}.${event.metadata.operation ?? 'updated'}`} values={{question}} components={{b: <b />}} />, changes: []};
  }
  const presentation = eventFormatters[event.type]?.(event, t, renderValue, renderPerson) ?? {
    title: t('boxActivity.updated'),
    changes: changesFor(event),
  };
  if (event.type === 'box.exclusions_updated') {
    const names = event.metadata.exclusionNames ?? {};
    return {
      title: organizerTitle(event, t, renderPerson, viewerCardId),
      description: <div className="box-activity__exclusions">{Object.entries(event.metadata.after ?? {}).map(([id, value]) => {
        const excluded = String(value || '').split(',').filter(Boolean);
        return <p key={id}>
          {renderIdentity ? renderIdentity(Number(id), names[id] || t('boxActivity.participant'), event.metadata.exclusionParticipants?.[id]) : <b>{names[id]}</b>}
          {' '}{excluded.length ? t('boxActivity.doesNotGiveTo') : t('boxActivity.exclusionsRemoved')}{' '}
          {excluded.map((target, index) => <span key={target}>{index > 0 && ', '}<b>{names[target] || t('boxActivity.participant')}</b></span>)}
        </p>;
      })}</div>, changes: [],
    };
  }
  if (event.type === 'box.settings_updated') {
    const title = t('boxActivity.events.box.settings_updated');
    const groups = new Map<string, string[]>();
    for (const [field] of changesFor(event)) {
      const group = ['useCashLimit', 'cashLimitMin', 'cashLimit', 'cashLimitCurrency'].includes(field)
        ? 'budget' : ['pictureId', 'logo'].includes(field) ? 'picture' : field;
      groups.set(group, [...(groups.get(group) || []), field]);
    }
    const budgetValue = (settings: Record<string, Value>) => {
      if (settings.useCashLimit === false) return t('boxActivity.no');
      const amount = (value: Value | undefined) => typeof value === 'number' ? renderValue('budgetAmount', value) : '—';
      const currency = typeof settings.cashLimitCurrency === 'string'
        ? getCurrencySymbol(settings.cashLimitCurrency) || settings.cashLimitCurrency.toUpperCase() : '';
      return <>{amount(settings.cashLimitMin)} - {amount(settings.cashLimit)} {currency}</>;
    };
    const details = [...groups.entries()].map(([group, fields]) => {
      const side = (which: 'before' | 'after') => {
        const values = event.metadata[which] ?? {};
        const theme = which === 'before' ? event.metadata.previousTheme || event.metadata.theme : event.metadata.theme;
        if (group === 'budget') return budgetValue(event.metadata[which === 'before' ? 'budgetBefore' : 'budgetAfter'] ?? values);
        if (group === 'picture') {
          const logo = 'logo' in values ? values.logo : event.metadata.logo;
          return renderValue(logo ? 'logo' : 'pictureId', logo || values.pictureId || event.metadata.pictureId || null, theme);
        }
        return renderValue(fields[0], values[fields[0]] ?? null, theme);
      };
      return <div key={group}>
        <dt>{t(group === 'budget' ? 'boxActivity.budget' : `boxActivity.fields.${group === 'picture' ? 'pictureId' : fields[0]}`, { defaultValue: fields[0] })}</dt>
        <dd className="box-activity__setting-values"><del>{side('before')}</del><span>{side('after')}</span></dd>
      </div>;
    });
    return { title: organizerTitle(event, t, renderPerson, viewerCardId) ?? title,
      description: <dl className="box-activity__initial-settings">{details}</dl>, changes: [] };
  }
  return { ...presentation, title: organizerTitle(event, t, renderPerson, viewerCardId) ?? presentation.title };
}
